<template>
  <v-dialog
    v-model="isOpen"
    :fullscreen="isFullscreen"
    :max-width="isFullscreen ? undefined : 1200"
    :hide-overlay="false"
    persistent
    no-click-animation
  >
    <v-card
      v-if="webinar"
      class="webinar-modal d-flex flex-column"
      :class="{ 'rounded-0': isFullscreen }"
    >
      <!-- ── Header ──────────────────────────────────────────────────── -->
      <v-card-title class="webinar-modal__header pa-3">
        <span class="text-subtitle-1 font-weight-bold text-truncate flex-grow-1">
          {{ webinar.title }}
        </span>
        <div class="d-flex align-center ml-2">
          <!-- Minimize -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                data-qa="webinar-modal-minimize"
                v-on="on"
                @click="handleMinimize"
              >
                <v-icon small>
                  mdi-minus
                </v-icon>
              </v-btn>
            </template>
            <span>Minimize</span>
          </v-tooltip>

          <!-- Toggle fullscreen -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                data-qa="webinar-modal-fullscreen"
                v-on="on"
                @click="toggleFullscreen"
              >
                <v-icon small>
                  {{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ isFullscreen ? 'Exit Full Screen' : 'Full Screen' }}</span>
          </v-tooltip>

          <!-- Close -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                data-qa="webinar-modal-close"
                v-on="on"
                @click="handleClose"
              >
                <v-icon small>
                  mdi-close
                </v-icon>
              </v-btn>
            </template>
            <span>Close</span>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-divider />

      <!-- ── Body: player (9 cols) + chat panel (3 cols) ────────────── -->
      <v-card-text
        class="webinar-modal__body flex-grow-1 pa-0"
        :class="{ 'webinar-modal__body--fullscreen': isFullscreen }"
      >
        <v-row
          no-gutters
          class="fill-height"
        >
          <!-- Player column -->
          <v-col
            cols="12"
            md="9"
            class="webinar-modal__player-col"
          >
            <!-- Wistia player via iframe (bypasses CSP nonce restrictions) -->
            <iframe
              v-if="mediaId"
              :src="`https://fast.wistia.net/embed/iframe/${mediaId}?autoPlay=true`"
              class="webinar-modal__player"
              allow="autoplay; fullscreen"
              allowfullscreen
              frameborder="0"
              @load="onPlayerLoad"
            />

            <!-- Nothing available -->
            <div
              v-else
              class="webinar-modal__no-embed d-flex align-center justify-center"
            >
              <v-icon
                color="grey darken-1"
                large
              >
                mdi-video-off-outline
              </v-icon>
            </div>
          </v-col>

          <!-- Chat panel column -->
          <v-col
            cols="12"
            md="3"
            class="webinar-modal__chat-col"
          >
            <WebinarChatPanel
              v-if="webinar.id"
              :webinar-id="webinar.id"
              :moderator-id="webinar.moderator && webinar.moderator.id"
              :host-id="webinar.host && webinar.host.id"
              :panelist-ids="webinar.panelists ? webinar.panelists.map(p => p.id) : []"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { extractWistiaMediaId, apiLogWebinarEvent } from '@/api/videos';
import WebinarChatPanel from './chat/WebinarChatPanel.vue';

export default {
  name: 'WebinarModal',

  components: { WebinarChatPanel },

  data() {
    return {
      isFullscreen: false,
      mediaId: null,
      streamOpenLogged: false,
    };
  },

  computed: {
    ...mapGetters('videos', ['isModalOpen', 'activeWebinar']),

    isOpen: {
      get() { return this.isModalOpen; },
      set(val) { if (!val) this.closeModal(); },
    },

    webinar() { return this.activeWebinar; },
  },

  watch: {
    webinar: {
      immediate: true,
      handler(w) {
        this.mediaId = extractWistiaMediaId(w?.wistia_url);
        if (w) {
          this.streamOpenLogged = false;
          this.logEvent('join_click');
        }
      },
    },

    isModalOpen(newVal) {
      if (!newVal) {
        this.isFullscreen = false;
        this.logEvent('stream_close');
      }
    },
  },

  methods: {
    ...mapActions('videos', ['closeModal', 'minimize']),

    handleClose() {
      this.isFullscreen = false;
      this.closeModal();
    },

    handleMinimize() {
      if (this.webinar) this.minimize(this.webinar.id);
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },

    onPlayerLoad() {
      if (!this.streamOpenLogged && this.webinar) {
        this.streamOpenLogged = true;
        this.logEvent('stream_open');
      }
    },

    logEvent(eventType) {
      if (!this.webinar) return;
      apiLogWebinarEvent({ webinar_id: this.webinar.id, event_type: eventType }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.webinar-modal {
  &__header {
    background-color: #ffffff;
    color: #000000;
  }

  &__body {
    background: #000;
    display: flex;
    // Non-fullscreen: set explicit height so chat column can fill properly
    height: 65vh;

    &--fullscreen {
      height: calc(100vh - 56px);
    }
  }

  &__player-col {
    background: #000;
    display: flex;
    align-items: stretch;
  }

  // Wistia player iframe fills its column
  &__player {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    border: none;

    @media (min-width: 960px) {
      aspect-ratio: unset;
      height: 100%;
    }
  }

  &__no-embed {
    min-height: 300px;
    flex: 1;
  }

  &__chat-col {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 959px) {
      min-height: 320px;
      max-height: 400px;
    }
  }
}
</style>
